import type { LucideIcon } from "lucide-react";
import { Send, Sun, Sunrise } from "lucide-react";
import type { Dayjs } from "dayjs";
import dayjs from "@punchup/utils/dayjs";

// ─── Types ───

export interface Preset {
  label: string;
  sub: string;
  icon: LucideIcon;
  date: Dayjs | null;
}

export interface TimeSlot {
  h: number;
  m: number;
}

// ─── Formatting helpers (all dayjs-based) ───

export function formatTime12(h: number, m: number): string {
  return dayjs().hour(h).minute(m).format("h:mm A");
}

export function formatDateShort(d: Dayjs): string {
  return d.format("ddd, MMM D");
}

export function formatFull(d: Dayjs): string {
  return d.format("ddd, MMM D [at] h:mm A");
}

// ─── Preset generation ───

export const TOMORROW_MORNING_HOUR = 9;
export const TOMORROW_AFTERNOON_HOUR = 13;

/** Tomorrow at `hour`:00 in the viewer's zone. */
export function tomorrowAt(now: Dayjs, hour: number): Dayjs {
  return now.add(1, "day").startOf("day").hour(hour);
}

export function generatePresets(now: Dayjs): Preset[] {
  const tmrwMorn = tomorrowAt(now, TOMORROW_MORNING_HOUR);
  const tmrwAftn = tomorrowAt(now, TOMORROW_AFTERNOON_HOUR);

  return [
    {
      label: "Immediately",
      sub: "Send right now",
      icon: Send,
      date: null,
    },
    {
      label: "Tomorrow morning",
      sub: formatTime12(TOMORROW_MORNING_HOUR, 0),
      icon: Sunrise,
      date: tmrwMorn,
    },
    {
      label: "Tomorrow afternoon",
      sub: formatTime12(TOMORROW_AFTERNOON_HOUR, 0),
      icon: Sun,
      date: tmrwAftn,
    },
  ];
}

// ─── Time-slot generation ───

export function generateTimeSlots(
  isToday: boolean,
  now: Dayjs,
  startHour = 6,
  endHour = 24
): TimeSlot[] {
  const slots: TimeSlot[] = [];

  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (
        isToday &&
        (h < now.hour() || (h === now.hour() && m <= now.minute()))
      ) {
        continue;
      }
      slots.push({ h, m });
    }
  }

  return slots;
}

// ─── Grouped time slots for Select dropdown ───

export interface GroupedTimeSlots {
  morning: TimeSlot[]; // 6:00 AM – 11:45 AM
  afternoon: TimeSlot[]; // 12:00 PM – 5:45 PM
  evening: TimeSlot[]; // 6:00 PM – 11:45 PM
}

export function groupTimeSlots(slots: TimeSlot[]): GroupedTimeSlots {
  return {
    morning: slots.filter((s) => s.h >= 6 && s.h < 12),
    afternoon: slots.filter((s) => s.h >= 12 && s.h < 18),
    evening: slots.filter((s) => s.h >= 18),
  };
}

// ─── Shared view helpers (used by both Mobile & Web scheduler views) ───

export function toTimeValue(h: number, m: number): string {
  return `${h}:${String(m).padStart(2, "0")}`;
}

export function parseTimeValue(value: string): { h: number; m: number } {
  const [h, m] = value.split(":").map(Number);
  return { h, m };
}

// ─── Timezone helpers ───

export interface UserTimezone {
  abbreviation: string; // e.g. "EST"
  fullName: string; // e.g. "Eastern Standard Time"
}

export function getUserTimezone(): UserTimezone {
  const abbreviation = dayjs().tz(dayjs.tz.guess()).format("z");

  const fullName =
    new Intl.DateTimeFormat("en-US", { timeZoneName: "long" })
      .formatToParts(dayjs().toDate())
      .find((p) => p.type === "timeZoneName")?.value ?? dayjs.tz.guess();

  return { abbreviation, fullName };
}
